import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const CounterItem = ({ number, text }: { number: string; text: string }) => (
  <motion.div 
    className="text-center relative group"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8 }}
  >
    <div className="relative">
      <div className="text-[120px] font-serif text-[#41392e] opacity-20 absolute -top-10 left-1/2 -translate-x-1/2">
        {number.split(' ')[0]}
      </div>
      <div className="relative z-10">
        <div className="text-4xl font-serif text-[#c9ab81] mb-2">{number}</div>
        <div className="text-sm uppercase tracking-[0.2em] text-gray-400 font-light">{text}</div>
      </div>
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-px bg-gradient-to-r from-transparent via-[#c9ab81] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </div>
  </motion.div>
);

const ParallaxText = () => {
  const { t } = useTranslation();
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 0.7], [1, 1, 0]);

  return (
    <motion.div 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ opacity }}
    >
      <div 
        className="absolute inset-0 bg-cover bg-center bg-fixed z-0"
        style={{
          backgroundImage: "url('https://res.cloudinary.com/dansvy9a5/image/upload/v1739113191/1736228427125_2_fhzy9u.jpg')"
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
      </div>

      <motion.div 
        className="relative z-10 max-w-4xl mx-auto text-center px-4"
        style={{ y }}
      >
        <motion.h2 
          className="text-5xl md:text-7xl font-serif mb-12 tracking-wide"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        >
          <span className="text-[#c9ab81] italic">{t('counter.title')}</span>
        </motion.h2>
        
        <motion.p 
          className="text-xl md:text-2xl font-light text-gray-300 leading-relaxed mb-12 tracking-wide"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {t('counter.description1')}
        </motion.p>
        
        <motion.p 
          className="text-xl md:text-2xl font-light text-gray-300 leading-relaxed tracking-wide"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
          viewport={{ once: true }}
        >
          {t('counter.description2')}
        </motion.p>
      </motion.div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent" />
    </motion.div>
  );
};

const Counter = () => {
  const { t } = useTranslation();
  
  const stats = [
    { number: "05", text: t('counter.stats.days') },
    { number: "10", text: t('counter.stats.places') },
    { number: "90%+", text: t('counter.stats.satisfaction') },
    { number: "∞", text: t('counter.stats.access') }
  ];

  return (
    <>
      <ParallaxText />
      
      <div className="relative py-32 section-transition section-transition-dark">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(154,165,129,0.1)_0%,rgba(0,0,0,0)_70%)]" />
        <motion.div 
          className="relative grid grid-cols-1 md:grid-cols-4 gap-16 max-w-7xl mx-auto px-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
            >
              <CounterItem {...stat} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </>
  );
};

export default Counter;